import classNames from 'classnames';
import { useDialogContext } from 'context/DialogContext';
import useIsMobileDevice from 'hooks/UseIsMobileDevice';
import React, { JSX, memo } from 'react';
import { SidebarRightMode, useSidebarRightContext } from 'context/SidebarRightContext';
import useScreenSize from 'hooks/UseScreenSize';
import { ScreenSize } from 'types/ScreenSize';
import touchEnabled from 'util/Util/TouchEnabled';
import Tooltip from '../Tooltip';
import Block from './Block';
import { useBannerContext } from 'context/BannerContext';
import { TextOutdentIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

interface Props {
  mobileMenuRef: React.RefObject<HTMLElement | null>;
}

function SidebarRight({ mobileMenuRef }: Props): JSX.Element {
  const { width } = useScreenSize();
  const { items, setIsOpen, isOpen, isHidden, width: sidebarWidth, mode, preview } = useSidebarRightContext();
  const { dialogsItems } = useDialogContext();
  const { bannerIsVisible } = useBannerContext();
  const isMobileDevice = useIsMobileDevice();
  const isTouchEnabled = touchEnabled();
  const { t } = useTranslation();

  return (
    <div
      // FIXME: REACT 19 - disable for now. Should fix Error: Cannot access refs during render
      // eslint-disable-next-line
      style={{ height: `calc(100vh - ${mobileMenuRef.current?.offsetHeight ?? 0}px + 1px)` }}
      className={classNames(
        'fixed right-0 z-10 border-l border-t transition-colors duration-300 bg-white border-gray-200',
        'top-0', // mobile
        {
          'md:top-[106px] md:!h-[calc(100vh-106px)]': !bannerIsVisible && mode === SidebarRightMode.Default, // non-mobile
          'md:top-[154px] md:!h-[calc(100vh-154px)]': bannerIsVisible && mode === SidebarRightMode.Default, // non-mobile
          'md:!h-full': !bannerIsVisible && mode !== SidebarRightMode.Default,
          'shadow-xl 2xl:shadow-none': isOpen,
          // we also hide the sidebar when there is a dialog op on mobile
          hidden: items.length === 0 || isHidden || (isMobileDevice && dialogsItems.length > 0),
        },
      )}
    >
      {mode === SidebarRightMode.Default ? (
        <div
          // Set the width depending on the state
          // for mobile we full
          style={{ width: sidebarWidth }}
          className={classNames(
            'flex flex-col h-full transition-fast transition-[width] relative overflow-hidden pt-[var(--safe-area-inset-top,_env(safe-area-inset-top,_0px))]',
            {
              'translate-x-[10px]': !isOpen && isMobileDevice, // on mobile we should remove the sidebar totally to the right
            },
          )}
        >
          <div
            style={{ visibility: width < ScreenSize['3xl'] ? 'visible' : 'hidden' }}
            className={classNames({
              'absolute z-50 right-1 top-[calc(4px+var(--safe-area-inset-top,_env(safe-area-inset-top,_0px)))]': isOpen,
              'border-b mb-1 flex justify-center': !isOpen,
            })}
          >
            <Tooltip
              disabled={isTouchEnabled}
              arrow={false}
              content={isOpen ? t('close-pane', 'close pane') : t('open-pane', 'open pane')}
              mode='dark'
              placement='left'
            >
              <button className='p-3' onClick={() => setIsOpen(prevState => !prevState)}>
                {/* with -scale-x-[1] we flip the icon */}
                <TextOutdentIcon size={16} className={classNames({ '-scale-x-[1]': isOpen })} />
              </button>
            </Tooltip>
          </div>
          {items.map(item => (
            <Block key={item.title} item={item} isOpen={isOpen} setIsOpen={setIsOpen} />
          ))}
        </div>
      ) : (
        <div
          // Set the width depending on the state
          // for mobile we full
          style={{ width: sidebarWidth }}
          className={classNames(
            'flex flex-col h-full transition-fast transition-[width] relative overflow-hidden pt-[var(--safe-area-inset-top,_env(safe-area-inset-top,_0px))]',
            {
              'translate-x-[10px]': !isOpen && isMobileDevice, // on mobile we should remove the sidebar totally to the right
            },
          )}
        >
          <div className='flex flex-col h-full'>{preview}</div>
        </div>
      )}
    </div>
  );
}

// avoid rerendering when props are changed but not value of the props
// this will mostly caused by the context API hooks
export default memo(SidebarRight);
